import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { db } from '../db';
import electronBridge from '../utils/electronBridge';

// Define the default menu items
const ALL_MENU_ITEMS = [
  { id: 'playground', label: 'Playground', to: '/', icon: 'GenerateIcon', activeIcon: 'GenerateIconActive' },
  { id: 'comfy', label: 'Comfy Output', to: '/comfy', icon: 'GenerateIcon', activeIcon: 'GenerateIconActive', conditionalRender: 'comfyVisibility' },
  { id: 'collection', label: 'My Collection', to: '/my-collection', icon: 'MyCollectionIcon', activeIcon: 'MyCollectionIconActive' },
  { id: 'templates', label: 'Templates', to: '/templates', icon: 'TemplatesIcon', activeIcon: 'TemplatesIconActive' },
  { id: 'profile', label: 'Profile', to: '/profile', icon: 'ProfileIcon', activeIcon: 'ProfileIconActive', dynamicLabel: true },
  { id: 'explore', label: 'Explore', to: '/explore', icon: 'ExploreIcon', activeIcon: 'ExploreIconActive' },
  { id: 'momm', label: 'MOMM', to: '/momm', icon: 'MOMMIcon', activeIcon: 'MOMMIconActive' },
  { id: 'settings', label: 'Settings', to: '/settings', icon: 'SettingsIcon', activeIcon: 'SettingsIconActive' },
  { id: 'help', label: 'Help', to: '/how-to', icon: 'HowToIcon', activeIcon: 'HowToIconActive' },
  { id: 'chat', label: 'Chat', to: '/chat', icon: 'ChatIcon', activeIcon: 'ChatIconActive' },
  { id: 'comfyui', label: 'Comfy UI', to: '/comfyui', icon: 'ComfyUIIcon', activeIcon: 'ComfyUIIconActive' },
];

// Filter out ComfyOutput and ComfyUI if not in Electron environment (but keep MOMM)
const DEFAULT_MENU_ITEMS = electronBridge.isElectron 
  ? ALL_MENU_ITEMS 
  : ALL_MENU_ITEMS.filter(item => item.id !== 'comfy');

const MenuStateContext = createContext();
const MenuDispatchContext = createContext();

const menuReducer = (state, action) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        menuItems: action.payload,
        isEditMode: true
      };
    case 'REORDER':
      return {
        ...state,
        menuItems: action.payload,
        isDirty: true
      };
    case 'TOGGLE_EDIT_MODE':
      return {
        ...state,
        isEditMode: !state.isEditMode
      };
    case 'SAVE_ORDER':
      return {
        ...state,
        isEditMode: true,
        isDirty: false
      };
    case 'SAVE_WITHOUT_EXIT':
      return {
        ...state,
        isDirty: false
      };
    case 'RESET_ORDER':
      return {
        ...state,
        menuItems: DEFAULT_MENU_ITEMS,
        isEditMode: true,
        isDirty: true
      };
    default:
      return state;
  }
};

export const MenuProvider = ({ children }) => {
  const [state, dispatch] = useReducer(menuReducer, {
    menuItems: DEFAULT_MENU_ITEMS,
    isEditMode: true,
    isDirty: false
  });

  // Load menu order from DB on first render
  useEffect(() => {
    const loadMenuOrder = async () => {
      try {
        const setting = await db.settings.where('key').equals('menu_order').first();
        if (setting && setting.value) {
          // Filter out ComfyOutput and ComfyUI in browser environment (but keep MOMM)
          const menuItems = electronBridge.isElectron 
            ? setting.value 
            : setting.value.filter(item => item.id !== 'comfy');
          dispatch({ type: 'INITIALIZE', payload: menuItems });
        } else {
          // Initialize with default order if setting doesn't exist
          await db.settings.put({ key: 'menu_order', value: DEFAULT_MENU_ITEMS });
        }
      } catch (error) {
        console.error('Error loading menu order:', error);
      }
    };

    loadMenuOrder();
  }, []);

  // Save menu order when it changes
  useEffect(() => {
    if (state.isDirty) {
      const saveMenuOrder = async () => {
        try {
          await db.settings.put({ key: 'menu_order', value: state.menuItems });
        } catch (error) {
          console.error('Error saving menu order:', error);
        }
      };

      saveMenuOrder();
    }
  }, [state.isDirty, state.menuItems]);

  return (
    <MenuStateContext.Provider value={state}>
      <MenuDispatchContext.Provider value={dispatch}>
        {children}
      </MenuDispatchContext.Provider>
    </MenuStateContext.Provider>
  );
};

export const useMenuState = () => {
  const context = useContext(MenuStateContext);
  if (context === undefined) {
    throw new Error('useMenuState must be used within a MenuProvider');
  }
  return context;
};

export const useMenuDispatch = () => {
  const context = useContext(MenuDispatchContext);
  if (context === undefined) {
    throw new Error('useMenuDispatch must be used within a MenuProvider');
  }
  return context;
};

export const getDefaultMenuItems = () => DEFAULT_MENU_ITEMS; 